import React from "react";
import { TbX, TbFileTypePdf, TbPresentation, TbLoader2 } from "react-icons/tb";
import { RATING_LABEL } from "../../lib/ratings";
import { initialsOf } from "../../lib/deliverable";
import { blankFor } from "../../lib/deliverableExport";
import { longDate, monthOf } from "./useDeliverable";
import { Button, EmptyState } from "../../components";
import "./Reviews.css";

// The client deliverable, as the design kit draws it.
//
// Rendered in the TENANT's report colours, never the product blue: an MSP hands this to
// their client, and Pipsqueak's accent appearing on it would undo the white-labelling.
// Both colours come from the tenant's brand_colors, with the kit's seeded palette as the
// fallback when a workspace has not set any.
//
// Used two ways: inline as Preview, and full-screen as Present.

// How the distribution under the score is ordered: the two unscored kinds sit at the end
// because they are the absence of a judgement, not a worse one.
const DOTS = ["red", "yellow", "green", "na", "unrated"];

// `deliverable` comes from useDeliverable, owned by the editor so the export works from
// anywhere in it. The page renders from it and the export buttons call into it.
const PresentationView = ({ assessment, counts, score, me, deliverable, onClose }) => {
  const {
    previous, progress, plan, primary, accent, tenant, summary, steps, findings, glance, delta,
    orgName, footerLine, contact, logo, coverTitle, coverLede, exportAs, exporting, exportError,
  } = deliverable;

  return (
    <div className={`v2-deliv${onClose ? " v2-deliv--full" : ""}`}
         style={{ "--report-primary": primary, "--report-accent": accent }}>
      {onClose && (
        <div className="v2-deliv-bar">
          <span className="v2-deliv-bar-title">{assessment.organization_name}</span>
          <Button icon aria-label="Leave presentation" style={{ marginLeft: "auto" }} onClick={onClose}>
            <TbX size={16} />
          </Button>
        </div>
      )}

      <div className="v2-deliv-sheet">
        {/* The tenant's band, with their mark and their colour. */}
        <div className="v2-deliv-head">
          {logo
            ? <img className="v2-deliv-logo" src={logo} alt="" />
            : <span className="v2-deliv-mark">{tenant.slice(0, 1).toUpperCase()}</span>}
          <span className="v2-deliv-tenant">{tenant}</span>
          {/* Real files, drawn from the same data as this page (lib/deliverableExport.js):
              a PDF with selectable text, and a deck of native, editable slides. */}
          <div className="v2-deliv-exports">
            <button type="button" className="v2-deliv-export" disabled={!!exporting} onClick={() => exportAs("pdf")}>
              {exporting === "pdf" ? <TbLoader2 size={15} className="v2-spin" /> : <TbFileTypePdf size={15} />}
              {exporting === "pdf" ? "Creating PDF…" : "Export PDF"}
            </button>
            <button type="button" className="v2-deliv-export" disabled={!!exporting} onClick={() => exportAs("pptx")}>
              {exporting === "pptx" ? <TbLoader2 size={15} className="v2-spin" /> : <TbPresentation size={15} />}
              {exporting === "pptx" ? "Creating slides…" : "Export PowerPoint"}
            </button>
          </div>
        </div>
        {exportError && <p className="v2-deliv-export-error" role="alert">{exportError}</p>}

        <header className="v2-deliv-cover">
          <div className="v2-deliv-cover-main">
            <div className="v2-deliv-eyebrow">{assessment.title}</div>
            <h1 className="v2-deliv-title">{coverTitle}</h1>
            <p className="v2-deliv-lede">{coverLede}</p>
            {/* The client's own mark. Initials rather than nothing: an empty square on a
                document addressed to them reads as a broken image. */}
            <div className="v2-deliv-client">{initialsOf(orgName)}</div>

            <div className="v2-deliv-prep">
              <strong>Prepared for</strong>
              <span>{orgName}</span>
              <small>{longDate(assessment.assessment_date)}</small>
              {me?.name && <small>Prepared by {me.name}</small>}
            </div>
          </div>

          <div className="v2-deliv-score">
            {/* The arc is the tenant's accent, not the rating colour: this is their
                document and the number is already qualified in words below it. */}
            <div className="v2-deliv-ring" role="img"
                 aria-label={score === null || score === undefined
                   ? "Not yet scored"
                   : `Overall score ${score} out of 100`}
                 style={{ "--score": score ?? 0 }}>
              <span>{score === null || score === undefined ? "." : score}</span>
            </div>
            <div className="v2-deliv-scorelabel">Overall score</div>
            <div className="v2-deliv-healthlabel">Technology health</div>

            {/* What the number is made of. Each dot carries its count and a title, so the
                score is never the only thing the reader has to trust. */}
            {counts && (
              <div className="v2-deliv-dist">
                {DOTS.map((r) => (
                  <span key={r} title={RATING_LABEL[r]}>
                    <i className={`v2-deliv-dot v2-deliv-dot--${r}`} aria-hidden="true" />
                    <b>{counts[r] ?? 0}</b>
                    <span className="v2-deliv-dist-sr">{RATING_LABEL[r]}</span>
                  </span>
                ))}
              </div>
            )}
            {delta !== null && delta !== undefined && (
              <div className={`v2-deliv-delta${delta < 0 ? " down" : ""}`}>
                {delta > 0 ? `up ${delta}` : delta < 0 ? `down ${Math.abs(delta)}` : "no change"}
                {monthOf(previous?.assessment_date) ? ` since ${monthOf(previous.assessment_date)}` : ""}
              </div>
            )}
          </div>
        </header>

        {progress && (
          <section className="v2-deliv-sec">
            <h2 className="v2-deliv-h2">Progress since the last review</h2>
            <div className="v2-deliv-stats">
              <div><b>{progress.tally.improved}</b><span>improved</span></div>
              <div><b>{progress.tally.regressed}</b><span>regressed</span></div>
              <div><b>{progress.tally.unchanged}</b><span>unchanged</span></div>
              <div><b>{progress.tally.new}</b><span>newly assessed</span></div>
            </div>
          </section>
        )}

        {/* Where things stand today ------------------------------------- */}
        <section className="v2-deliv-sec">
          <div className="v2-deliv-eyebrow">Your technology at a glance</div>
          <h2 className="v2-deliv-h2">Where things stand today</h2>

          <div className="v2-deliv-exec">
            <div className="v2-deliv-eyebrow">Executive summary</div>
            <p className="v2-deliv-exec-head">{summary.headline}</p>
            <p className="v2-deliv-exec-sub">{summary.summary}</p>
          </div>

          <div className="v2-deliv-legend" aria-label="Colour key">
            {["red", "yellow", "green", "na"].map((r) => (
              <span key={r} className={r}><i aria-hidden="true" />{RATING_LABEL[r]}</span>
            ))}
          </div>

          {glance.map(({ category, items: rows }) => (
            <div key={category.id} className="v2-deliv-cat">
              <h3 className="v2-deliv-cat-name">{category.name}</h3>
              <div className="v2-deliv-grid">
                {rows.map((i) => (
                  <article key={i.id} className={`v2-deliv-cell v2-deliv-cell--${i.rating}`}>
                    <strong>{i.name}</strong>
                    {i.rating !== "unrated" && <span className="v2-deliv-verdict">{RATING_LABEL[i.rating]}</span>}
                    {i.summary
                      ? <p>{i.summary}</p>
                      : <p className="v2-deliv-blank">{blankFor(i.rating)}</p>}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* What the results mean ------------------------------------------ */}
        <section className="v2-deliv-sec">
          <div className="v2-deliv-eyebrow">Findings &amp; recommendations</div>
          <h2 className="v2-deliv-h2">What the results mean</h2>

          {findings.length === 0 ? (
            <EmptyState title="Nothing to show yet">
              Rate at least one item and it will appear here, grouped by category.
            </EmptyState>
          ) : findings.map(({ category, items: rows }) => (
            <div key={category.id} className="v2-deliv-cat">
              <h3 className="v2-deliv-cat-name">{category.name}</h3>
              {rows.map((i) => (
                <article key={i.id} className={`v2-deliv-find v2-deliv-find--${i.rating}`}>
                  <div className="v2-deliv-find-head">
                    <i aria-hidden="true" />
                    <strong>{i.name}</strong>
                    <span className="v2-deliv-find-verdict">{RATING_LABEL[i.rating]}</span>
                  </div>
                  {i.summary && <p>{i.summary}</p>}
                  {i.business_impact && <p><b>Business impact:</b> {i.business_impact}</p>}
                  {i.recommendation && <p><b>Recommendation:</b> {i.recommendation}</p>}
                  {!i.summary && !i.business_impact && !i.recommendation && (
                    <p className="v2-deliv-blank">No statement written for this item yet.</p>
                  )}
                  {i.source === "scan" && (
                    <p className="v2-deliv-verified">Verified by external scan.</p>
                  )}
                </article>
              ))}
            </div>
          ))}
        </section>

        {/* Turn findings into action -------------------------------------- */}
        {steps.items.length > 0 && (
          <section className="v2-deliv-sec v2-deliv-sec--steps">
            <div className="v2-deliv-eyebrow">Recommended next steps</div>
            <h2 className="v2-deliv-h2">Turn findings into action</h2>
            <p className="v2-deliv-lede">
              Focus first on the actions that reduce the greatest current risk and create a
              clear owner and target date for each decision.
            </p>

            {/* Numbered by CSS counter, so the numbers cannot drift from the order. */}
            <ol className="v2-deliv-steps">
              {steps.items.map((i) => (
                <li key={i.id} className={i.rating}>
                  <div>
                    <strong>{i.name}</strong>
                    <p>{i.recommendation}</p>
                    {i.business_impact && <small>Why now: {i.business_impact}</small>}
                  </div>
                </li>
              ))}
            </ol>

            {steps.total > steps.items.length && (
              <p className="v2-deliv-steps-more">
                {steps.total - steps.items.length} additional recommendation
                {steps.total - steps.items.length === 1 ? " is" : "s are"} detailed in the
                findings section.
              </p>
            )}
          </section>
        )}

        {/* The one section the client is asked to agree to, so it only appears when the
            consultant has actually written it. */}
        {plan.trim() && (
          <section className="v2-deliv-sec">
            <h2 className="v2-deliv-h2">Agreed action plan</h2>
            <p className="v2-deliv-plan">{plan}</p>
          </section>
        )}

        {/* Says what the document is and is not. A client deliverable that reads as a
            guarantee is a liability; this is the reference's wording. */}
        <aside className="v2-deliv-disclaimer">
          This assessment provides technology guidance based on information available at the
          review date. It is not a guarantee against every operational or security risk.
        </aside>

        <footer className="v2-deliv-foot">
          <span>{footerLine}</span>
          {/* Falls back to the signed-in user only when the workspace has no contact
              details of its own — better a real address than an empty line. */}
          <span>{contact || me?.email || ""}</span>
        </footer>
      </div>
    </div>
  );
};

export default PresentationView;
